// Shared Knowledge Unit (KU) metadata: display names and detailed descriptions.
// Used by every view that shows KUs (heatmap, organization skills chart, KU risk)
// so the hover information stays identical across the app.
// Mapping from KU ID to display name
export const KU_NAMES = {
    "K1":  "Data Types",
    "K2":  "Operators and Decisions",
    "K3":  "Arrays",
    "K4":  "Loops",
    "K5":  "Methods and Encapsulation",
    "K6":  "Inheritance",
    "K7":  "Advanced Class Design",
    "K8":  "Generics and Collections",
    "K9":  "Functional Interfaces",
    "K10": "Stream API",
    "K11": "Exceptions",
    "K12": "Date Time API",
    "K13": "IO",
    "K14": "NIO",
    "K15": "String Processing",
    "K16": "Concurrency",
    "K17": "Databases",
    "K18": "Localization",
    "K19": "Java Persistence API",
    "K20": "Enterprise Java Beans",
    "K21": "Java Message Service API",
    "K22": "SOAP Web Services",
    "K23": "Servlets",
    "K24": "Java REST API",
    "K25": "Websockets",
    "K26": "Java Server Faces",
    "K27": "Contexts and Dependency Injection",
    "K28": "Batch Processing",
};

// Detailed tooltip descriptions per KU
export const KU_DESCRIPTIONS = {
    "K1":  "Declare and initialize different types of variables (e.g., primitive, parameterized and array type), including the casting of primitive data types.",
    "K2":  "Use Java operators (e.g., assignment and postfix operators); use parentheses to override operator precedence. Test equality between strings and other objects using == and equals(). Create and use if, if-else, and ternary constructs. Use the switch statement.",
    "K3":  "Declare, instantiate, initialize and use a one-dimensional array. Declare, instantiate, initialize and use a multi-dimensional array.",
    "K4":  "Create and use while loops. Create and use for loops, including the enhanced for loop. Create and use do-while loops. Use the break and continue statements.",
    "K5":  "Create methods with arguments and return values. Apply the static keyword. Create overloaded methods and constructors. Use variable length arguments. Use different access modifiers. Apply encapsulation with get/set methods and immutable classes.",
    "K6":  "Use basic polymorphism. Use polymorphic parameters. Create overridden methods. Create abstract classes and methods. Use super() and the super keyword. Use casting in referring a subclass object to a superclass object.",
    "K7":  "Create inner classes, including static inner classes, local classes, nested classes, and anonymous inner classes. Develop code that uses the final keyword. Use enumerated types including methods and constructors. Create singleton classes and immutable classes.",
    "K8":  "Create and use a generic class. Create and use ArrayList, TreeSet, TreeMap, and ArrayDeque. Use java.util.Comparator and java.lang.Comparable interfaces. Iterate using the forEach method of List.",
    "K9":  "Use the built-in interfaces in java.util.function such as Predicate, Consumer, Function, and Supplier. Develop code that uses primitive, binary, and UnaryOperator versions of functional interfaces.",
    "K10": "Extract data from an object using peek() and map() methods. Search for data using findFirst, findAny, anyMatch, allMatch, noneMatch. Use the Optional class. Sort a collection using the Stream API. Use collect() and flatMap() methods.",
    "K11": "Create a try-catch block. Use the catch, multi-catch, and finally clauses. Auto-close resources with try-with-resources. Create custom exceptions and autocloseable resources. Use common exception classes like NullPointerException, ArithmeticException, ArrayIndexOutOfBoundsException, ClassCastException.",
    "K12": "Create and manage date-based and time-based events using LocalDate, LocalTime, LocalDateTime, Instant, Period, Duration. Format date and time values with different timezones. Manipulate calendar data using classes from java.time.*",
    "K13": "Read and write data using the console. Use BufferedReader, BufferedWriter, File, FileReader, FileWriter, FileInputStream, FileOutputStream, ObjectOutputStream, ObjectInputStream, and PrintWriter from the java.io package.",
    "K14": "Use the Path interface to operate on file and directory paths. Use the Files class to check, read, delete, copy, move, and manage metadata of a file or directory.",
    "K15": "Search, parse and build strings. Manipulate data using the StringBuilder class and its methods. Use regular expressions using the Pattern and Matcher classes. Use string formatting.",
    "K16": "Create worker threads using Runnable, Callable and use an ExecutorService to concurrently execute tasks. Use the synchronized keyword and java.util.concurrent.atomic package. Use java.util.concurrent collections and classes including CyclicBarrier and CopyOnWriteArrayList. Use the parallel Fork/Join Framework.",
    "K17": "Describe the interfaces that make up the core of the JDBC API, including the Driver, Connection, Statement, and ResultSet interfaces. Submit queries and read results from the database.",
    "K18": "Read and set the locale by using the Locale object. Build a resource bundle for each locale and load a resource bundle in an application.",
    "K19": "Create JPA Entities and Object-Relational Mappings (ORM). Use EntityManager to perform database operations, transactions, and locking with JPA entities. Create and execute JPQL statements.",
    "K20": "Create session EJB components containing synchronous and asynchronous business methods, manage the life cycle container callbacks, and use interceptors. Create EJB timers.",
    "K21": "Implement Java EE message producers and consumers, including message-driven beans. Use transactions with the JMS API.",
    "K22": "Create SOAP Web Services and Clients using the JAX-WS API. Create, marshall and unmarshall Java Objects by using the JAXB API.",
    "K23": "Create Java Servlets and use HTTP methods. Handle HTTP headers, parameters and cookies. Manage servlet life cycle with container callback methods and WebFilters.",
    "K24": "Apply REST service conventions. Create REST Services and clients using the JAX-RS API.",
    "K25": "Create WebSocket Server and Client Endpoint Handlers. Produce and consume, encode and decode WebSocket messages.",
    "K26": "Use JSF syntax and JSF Tag Libraries. Handle localization and produce messages. Use Expression Language (EL) and interact with CDI beans.",
    "K27": "Create CDI Bean Qualifiers, Producers, Disposers, Interceptors, Events, and Stereotypes.",
    "K28": "Implement batch jobs using the JSR 352 API.",
};

// Helper: given a raw KU id like "ku_1" or "K1", return the canonical key "K1"
export const normalizeKuId = (rawId) => {
    // Handle formats: "ku_1", "KU_1", "K1", "k1", "1"
    const match = String(rawId).match(/(\d+)$/);
    if (match) return `K${match[1]}`;
    return rawId;
};

// Helper: "K8" -> "K8 \u2013 Generics and Collections" (falls back to the raw id)
export const getKuLabel = (rawId) => {
    const key = normalizeKuId(rawId);
    return KU_NAMES[key] ? `${key} \u2013 ${KU_NAMES[key]}` : String(rawId);
};

// Helper: description text for a KU id, or "" when unknown
export const getKuDescription = (rawId) => KU_DESCRIPTIONS[normalizeKuId(rawId)] || "";

// ─────────────────────────────────────────────────────────────────────────
// Eclipse / IoT taxonomy (KU-Detection "detector17" model, 17 KUs).
// These ids are textual (e.g. "MQTT", "IEC_61499") so they must NOT go through
// normalizeKuId, which would turn "IEC_61499" into "K61499".
// ─────────────────────────────────────────────────────────────────────────
export const ECLIPSE_KU_IDS = [
    "MQTT",
    "REST_APIs",
    "Concurrency",
    "WebSockets",
    "Messaging_APIs",
    "OPC_UA",
    "Telemetry_Data",
    "Edge_Computing",
    "OTA_Updates",
    "IoT_Security",
    "EMF",
    "Xtext",
    "Eclipse_RCP",
    "Embedded_RTOS",
    "Matter_Thread",
    "IEC_61499",
    "CMake",
];

export const ECLIPSE_KU_NAMES = {
    "MQTT":           "MQTT",
    "REST_APIs":      "REST APIs",
    "Concurrency":    "Concurrency",
    "WebSockets":     "WebSockets",
    "Messaging_APIs": "Messaging APIs",
    "OPC_UA":         "OPC UA",
    "Telemetry_Data": "Telemetry Data",
    "Edge_Computing": "Edge Computing",
    "OTA_Updates":    "OTA Updates",
    "IoT_Security":   "IoT Security",
    "EMF":            "Eclipse Modeling Framework (EMF)",
    "Xtext":          "Xtext",
    "Eclipse_RCP":    "Eclipse Rich Client Platform (RCP)",
    "Embedded_RTOS":  "Embedded RTOS",
    "Matter_Thread":  "Matter / Thread",
    "IEC_61499":      "IEC 61499",
    "CMake":          "CMake",
};

export const ECLIPSE_KU_DESCRIPTIONS = {
    "MQTT":           "Publish/subscribe messaging with MQTT brokers and clients (e.g., Eclipse Paho, Mosquitto): connections, topics, QoS levels, retained messages and last-will handling.",
    "REST_APIs":      "Design and consume RESTful HTTP APIs: resources, HTTP methods and status codes, request/response serialization and client/server frameworks.",
    "Concurrency":    "Multi-threaded and asynchronous programming: threads, executors, locks, atomics, futures and thread-safe data structures.",
    "WebSockets":     "Full-duplex communication over WebSockets: server and client endpoints, message encoding/decoding and connection lifecycle.",
    "Messaging_APIs": "Message-oriented middleware and brokers (e.g., JMS, AMQP, Kafka, CoAP, DDS): producers, consumers, queues and topics.",
    "OPC_UA":         "Industrial interoperability with OPC UA (e.g., Eclipse Milo, open62541): information models, nodes, sessions, subscriptions and security policies.",
    "Telemetry_Data": "Collecting, encoding and transmitting device telemetry and sensor data: metrics, time series, payload formats and reporting.",
    "Edge_Computing": "Processing data close to devices on edge nodes and gateways: local analytics, offloading, device management and edge runtimes.",
    "OTA_Updates":    "Over-the-air firmware and software updates: update packaging, delivery, verification, rollback and device provisioning (e.g., Eclipse hawkBit).",
    "IoT_Security":   "Securing connected devices and their communication: TLS/DTLS, certificates, authentication, encryption and secure boot.",
    "EMF":            "Model-driven engineering with the Eclipse Modeling Framework: Ecore metamodels, generated model code, resources and model manipulation.",
    "Xtext":          "Building domain-specific languages with Xtext: grammars, parsers, scoping, validation, code generation and editor integration.",
    "Eclipse_RCP":    "Developing Eclipse plug-ins and Rich Client Platform applications: OSGi bundles, extension points, views, editors, commands and SWT/JFace UI.",
    "Embedded_RTOS":  "Programming embedded systems on real-time operating systems (e.g., FreeRTOS, Zephyr): tasks, scheduling, interrupts, drivers and device trees.",
    "Matter_Thread":  "Smart-home connectivity with the Matter standard and the Thread mesh network protocol: commissioning, clusters and device interoperability.",
    "IEC_61499":      "Distributed industrial automation with IEC 61499 (e.g., Eclipse 4diac): function blocks, applications, event/data connections and device mapping.",
    "CMake":          "Configuring and building native projects with CMake: CMakeLists, targets, dependencies, toolchains and cross-compilation.",
};
